import { useMemo } from "react";
import { RefreshCw, TrendingUp, TrendingDown, CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, PieChart, Pie, ComposedChart, Area, AreaChart } from 'recharts';
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { format, subDays } from "date-fns";

const cfBlue = '#0084FF';
const cfGreen = '#10B981';
const cfOrange = '#FFB84D';
const cfRed = '#DC2626';
const cfPurple = '#5B2C93';

interface ApprovalMetric {
  label: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
  /** Sparkline data points rendered as a wave at the bottom of the card */
  spark?: number[];
}

interface ApprovalDashboardProps {
  stats?: {
    totalPending?: number;
    approvedThisPeriod?: number;
    rejectedThisPeriod?: number;
    pending?: number;
    completedToday?: number;
    awaitingOthers?: number;
    [key: string]: any;
  };
  dailyTrend?: any[];
  statusDistribution?: any[];
  approverPerformance?: any[];
  isLoading?: boolean;
  onRefresh?: () => void;
  /** Which part to render: "boxes" (header + metric cards), "charts", or "all" (default) */
  section?: "boxes" | "charts" | "all";
}

function MetricCard({
  label,
  value,
  change,
  icon,
  color,
  trend,
  spark
}: ApprovalMetric) {
  const gradId = `spark-${color.replace('#', '')}`;
  return (
    <div
      className="relative overflow-hidden border rounded-lg p-4 pl-5 pb-0 flex flex-col transition-shadow hover:shadow-sm"
      style={{
        background: `linear-gradient(135deg, ${color}1A 0%, ${color}0A 100%)`,
        borderColor: `${color}33`,
      }}
    >
      <span
        className="absolute left-0 top-0 h-full w-1.5"
        style={{ background: color }}
      />
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
          {label}
        </span>
        <div
          className="p-2 rounded-lg"
          style={{ backgroundColor: `${color}26` }}
        >
          <div style={{ color }}>
            {icon}
          </div>
        </div>
      </div>
      <div className="text-3xl font-bold text-[#1e293b] mb-1">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {change !== undefined && (
        <div className={cn(
          "text-xs font-semibold flex items-center gap-1",
          trend === 'up' ? "text-[#10B981]" : trend === 'down' ? "text-[#DC2626]" : "text-[#64748b]"
        )}>
          {trend === 'up' && <TrendingUp className="h-3 w-3" />}
          {trend === 'down' && <TrendingDown className="h-3 w-3" />}
          {trend === 'up' ? '↑' : trend === 'down' ? '↓' : ''} {Math.abs(change)}% vs last period
        </div>
      )}

      {/* Wave sparkline pinned to the bottom edge */}
      {spark && spark.length > 0 && (
        <div className="mt-3 -ml-5 -mr-4 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark.map((v) => ({ v }))} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke={color}
                strokeWidth={1.5}
                fill={`url(#${gradId})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// Generate demo data for charts.
// Deterministic wave-shaped curves so the trend lines read as smooth waves
// (same chart types as before, just different underlying data).
function generateDemoTrendData() {
  const data = [];
  for (let i = 13; i >= 0; i--) {
    const date = subDays(new Date(), i);
    const t = 13 - i;
    // Approval Trend (lines): funnel of requests through the pipeline
    const submitted = Math.round(40 + 14 * Math.sin(t / 1.6) + 5 * Math.cos(t / 0.8));
    const completed = Math.round(30 + 11 * Math.sin(t / 1.4 + 0.5));
    const pending = Math.round(16 + 8 * Math.cos(t / 1.9) + 3 * Math.sin(t / 0.7));
    // Daily Approval Volume (bars): approvals by level
    const l1 = Math.round(18 + 7 * Math.sin(t / 1.2));
    const l2 = Math.round(10 + 5 * Math.cos(t / 1.5 + 1));
    const auto = Math.round(8 + 4 * Math.sin(t / 0.9 + 2));
    data.push({
      date: format(date, 'MMM d'),
      submitted: Math.max(4, submitted),
      completed: Math.max(3, completed),
      pending: Math.max(2, pending),
      l1: Math.max(2, l1),
      l2: Math.max(1, l2),
      auto: Math.max(1, auto),
    });
  }
  return data;
}

function generateDemoStatusDistribution() {
  return [
    { label: 'Approved', count: 184, color: cfGreen },
    { label: 'Pending', count: 51, color: cfOrange },
    { label: 'Rejected', count: 19, color: cfRed },
    { label: 'Escalated', count: 12, color: cfPurple },
    { label: 'Withdrawn', count: 7, color: '#94A3B8' },
  ];
}

function generateDemoApproverPerformance() {
  return [
    { name: 'Ahmed Al-Mansouri', count: 94 },
    { name: 'Fatima Al-Dosari', count: 81 },
    { name: 'Mohammed Al-Otaibi', count: 69 },
    { name: 'Sara Al-Harbi', count: 58 },
    { name: 'Omar Al-Qahtani', count: 47 },
  ];
}

export default function ApprovalDashboard({
  stats,
  dailyTrend,
  statusDistribution,
  approverPerformance,
  isLoading = false,
  onRefresh,
  section = "all"
}: ApprovalDashboardProps) {
  const { t } = useTranslation();
  const showBoxes = section === "boxes" || section === "all";
  const showCharts = section === "charts" || section === "all";

  // Generate demo data if not provided
  const finalDailyTrend = useMemo(() => {
    return (dailyTrend && dailyTrend.length > 0) ? dailyTrend : generateDemoTrendData();
  }, [dailyTrend]);

  const finalStatusDistribution = useMemo(() => {
    return (statusDistribution && statusDistribution.length > 0) ? statusDistribution : generateDemoStatusDistribution();
  }, [statusDistribution]);

  const finalApproverPerformance = useMemo(() => {
    return (approverPerformance && approverPerformance.length > 0) ? approverPerformance : generateDemoApproverPerformance();
  }, [approverPerformance]);

  // Compute metrics
  const metrics: ApprovalMetric[] = useMemo(() => {
    const pending = stats?.totalPending || 0;
    const approved = stats?.approvedThisPeriod || 0;
    const rejected = stats?.rejectedThisPeriod || 0;
    const total = approved + rejected;

    const approvalRate = total > 0
      ? Math.round((approved / total) * 100)
      : 0;

    return [
      {
        label: t('approvals.pendingReview', 'Pending Review'),
        value: pending,
        change: stats?.pendingChange || -12,
        icon: <Clock className="h-4 w-4" />,
        color: cfOrange,
        trend: 'down',
        spark: [8, 12, 9, 15, 11, 18, 14, 10, 7, 13, 9, 6, 8, 5]
      },
      {
        label: t('approvals.approved', 'Approved'),
        value: approved || 156,
        change: stats?.approvedChange || 18,
        icon: <CheckCircle2 className="h-4 w-4" />,
        color: cfGreen,
        trend: 'up',
        spark: [10, 14, 12, 18, 16, 22, 20, 26, 24, 30, 28, 34, 32, 38]
      },
      {
        label: t('approvals.rejected', 'Rejected'),
        value: rejected || 23,
        change: stats?.rejectedChange || -5,
        icon: <XCircle className="h-4 w-4" />,
        color: cfRed,
        trend: 'down',
        spark: [6, 4, 7, 5, 8, 4, 6, 3, 5, 7, 4, 6, 5, 3]
      },
      {
        label: t('approvals.approvalRate', 'Approval Rate'),
        value: `${approvalRate || 87}%`,
        change: stats?.rateChange || 3,
        icon: <TrendingUp className="h-4 w-4" />,
        color: cfBlue,
        trend: 'up',
        spark: [80, 82, 81, 84, 83, 85, 84, 86, 85, 87, 86, 88, 87, 89]
      },
    ];
  }, [stats, t]);

  if (isLoading) {
    return (
      <div className="space-y-6 mb-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-24" />
        </div>
        {/* Metrics grid skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-8">
      {showBoxes && (
        <>
          {/* Header */}
          <div className="flex items-start justify-between gap-4 pt-6 md:pt-8">
            <div>
              <h2 className="text-2xl font-bold text-[#1a1530]">Approval Analytics</h2>
              <p className="text-[13.5px] text-[#64748b] mt-1">
                Track approval performance, trends, and request outcomes across all sites.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              className="gap-1.5 h-8 px-3 text-xs"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((metric, idx) => (
              <MetricCard key={idx} {...metric} />
            ))}
          </div>
        </>
      )}

      {/* Charts Grid */}
      {showCharts && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Approval Trend Chart */}
        <div className="bg-white border border-[#e5e7eb] rounded-lg p-6">
          <h3 className="text-sm font-bold text-[#1e293b] mb-4">Approval Trend</h3>
          {finalDailyTrend && finalDailyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={finalDailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: '12px' }} />
                <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="submitted"
                  stroke={cfBlue}
                  name="Submitted"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke={cfGreen}
                  name="Completed"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="pending"
                  stroke={cfOrange}
                  name="Pending"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-[#64748b]">
              No data available
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="bg-white border border-[#e5e7eb] rounded-lg p-6">
          <h3 className="text-sm font-bold text-[#1e293b] mb-4">Status Distribution</h3>
          {finalStatusDistribution && finalStatusDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={finalStatusDistribution}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {finalStatusDistribution.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color || cfBlue} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-[#64748b]">
              No data available
            </div>
          )}
        </div>

        {/* Daily Volume Bar Chart */}
        {finalDailyTrend && finalDailyTrend.length > 0 && (
          <div className="bg-white border border-[#e5e7eb] rounded-lg p-6 lg:col-span-2">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Daily Approval Volume</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={finalDailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: '12px' }} />
                <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                />
                <Legend />
                <Bar dataKey="l1" fill={cfPurple} name="L1 Approvals" />
                <Bar dataKey="l2" fill={cfBlue} name="L2 Approvals" />
                <Bar dataKey="auto" fill={cfGreen} name="Auto-Approved" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Approver Performance */}
        {finalApproverPerformance && finalApproverPerformance.length > 0 && (
          <div className="bg-white border border-[#e5e7eb] rounded-lg p-6 lg:col-span-2">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Approver Performance</h3>
            <div className="space-y-3">
              {finalApproverPerformance.slice(0, 5).map((approver: any, idx: number) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-[#1e293b] font-medium">{approver.name}</span>
                    <span className="text-xs text-[#64748b]">{approver.count} tasks</span>
                  </div>
                  <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min((approver.count / (finalApproverPerformance[0]?.count || 1)) * 100, 100)}%`,
                        backgroundColor: cfBlue
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}